import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import AdminLayout from '@/components/layouts/AdminLayout';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Loader2, 
  Save, 
  Eye, 
  ChevronLeft, 
  CloudUpload, 
  X,
  Plus
} from 'lucide-react';
import * as blogApi from '@/api/blogApi';
import { Blog, BlogCategory, CreateBlogDto, UpdateBlogDto, CreateBlogCategoryDto } from '@/types/blog';
import RichTextEditor from '@/components/editor/RichTextEditor';
import ImageUploader from '@/components/shared/ImageUploader';
import { supabase } from '@/integrations/supabase/client';
import AuthCheck from '@/components/auth/AuthCheck';
import SlugField from '@/components/shared/SlugField';

// Import a modern text editor (using placeholder for now)
// In a real implementation, you would import a rich text editor like TipTap, CKEditor, etc.
// For example: import { useEditor, EditorContent } from '@tiptap/react';
// and add the necessary extensions

const BlogEditor = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blog, setBlog] = useState<Blog | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  
  // Form fields
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  
  // New category form
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  // Generate slug from category name
  const generateCategorySlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Handle creating a new category
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      setAddingCategory(true);
      
      const categoryData: CreateBlogCategoryDto = {
        name: newCategoryName.trim(),
        slug: generateCategorySlug(newCategoryName),
      };
      
      console.log('Creating new category:', categoryData);
      
      const newCategory = await blogApi.createCategory(categoryData);
      
      console.log('New category created:', newCategory);
      
      if (!newCategory || !newCategory.id) {
        throw new Error('Failed to create category: Invalid response from server');
      }
      
      // Update categories list
      setCategories(prev => [...prev, newCategory]);
      
      // Select the new category
      setSelectedCategoryIds(prev => [...prev, newCategory.id]);
      
      // Reset form
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      
      toast.success('Category created and added successfully');
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(`Failed to create category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAddingCategory(false);
    }
  };

  // Auto-generate slug from title
  const generateSlug = async () => {
    if (!title) {
      toast.error('Please enter a title first');
      return;
    }
    
    try {
      setLoading(true);
      const { slug: generatedSlug } = await blogApi.generateUniqueSlug(title, isEditing ? id : undefined);
      setSlug(generatedSlug);
      toast.success('Slug generated successfully');
    } catch (error) {
      console.error('Error generating slug:', error);
      // Set a basic slug as fallback if all else fails
      const fallbackSlug = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      setSlug(fallbackSlug);
      toast.error('Error generating optimized slug. Using basic version instead.');
    } finally {
      setLoading(false);
    }
  };

  // Load blog data if editing
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load categories
        const categoriesData = await blogApi.getCategories();
        setCategories(categoriesData);
        
        // Load blog if editing
        if (isEditing && id) {
          const blogData = await blogApi.getBlogById(id);
          if (blogData) {
            setBlog(blogData);
            setTitle(blogData.title);
            setSlug(blogData.slug);
            setContent(blogData.content);
            setExcerpt(blogData.excerpt || '');
            setFeaturedImage(blogData.featured_image || '');
            setStatus(blogData.status as 'draft' | 'published');
            
            // Set selected categories
            if (blogData.categories && blogData.categories.length > 0) {
              setSelectedCategoryIds(blogData.categories.map((cat: BlogCategory) => cat.id));
            }
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isEditing]);

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Starting blog submission...');
    
    if (!title || !content) {
      toast.error('Title and content are required');
      return false;
    }
    
    try {
      setSaving(true);
      console.log('Setting saving state to true');
      
      // Simple local slug generation if needed
      const slugToUse = slug || title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\s-]/g, '');
      console.log('Using slug:', slugToUse);
      
      const blogData = {
        title,
        slug: slugToUse,
        content,
        excerpt: excerpt || undefined,
        featured_image: featuredImage || undefined,
        status: status as 'draft' | 'published',
        category_ids: selectedCategoryIds
      };
      
      if (isEditing && id) {
        // Update existing blog post
        console.log('Updating existing blog with ID:', id);
        
        const updatedBlog = await blogApi.updateBlog(id, blogData);
        console.log('Blog updated successfully:', updatedBlog);
        toast.success('Blog post updated successfully');
      } else {
        // Create new blog post
        console.log('Creating new blog post');
        
        if (!user?.id) {
          throw new Error('User ID is required to create a blog post');
        }
        
        const newBlog = await blogApi.createBlog(blogData, user.id);
        console.log('Blog created successfully:', newBlog);
        toast.success('Blog post created successfully');
      }
      
      // No navigation here anymore
      return true;
    } catch (error) {
      console.error('Error in blog submission:', error);
      toast.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    } finally {
      setSaving(false);
      console.log('Setting saving state to false');
    }
  };

  // Save without navigation (for preview)
  const saveWithoutNavigation = async (): Promise<boolean> => {
    console.log('Saving without navigation...');
    
    if (!title || !content) {
      toast.error('Title and content are required');
      return false;
    }
    
    try {
      setSaving(true);
      
      // Simple local slug generation if needed
      const slugToUse = slug || title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\s-]/g, '');
      
      const blogData = {
        title,
        slug: slugToUse,
        content,
        excerpt: excerpt || undefined,
        featured_image: featuredImage || undefined,
        status: status as 'draft' | 'published',
        category_ids: selectedCategoryIds
      };
      
      if (isEditing && id) {
        // Update existing blog post
        await blogApi.updateBlog(id, blogData);
        toast.success('Changes saved for preview');
      } else {
        // Create new blog post
        if (!user?.id) {
          throw new Error('User ID is required to create a blog post');
        }
        
        const newBlog = await blogApi.createBlog(blogData, user.id);
        toast.success('Draft created for preview');
      }
      
      return true;
    } catch (error) {
      console.error('Error saving for preview:', error);
      toast.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Special function just for preview button 
  const handlePreview = async (e: React.MouseEvent) => {
    // Prevent default behavior more aggressively
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!title || !content) {
      toast.error('Title and content are required for preview');
      return;
    }
    
    try {
      setSaving(true);
      toast.info('Saving changes before preview...');
      
      // Prepare data
      const slugToUse = slug || title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\s-]/g, '');
      
      const blogData = {
        title,
        slug: slugToUse,
        content,
        excerpt: excerpt || undefined,
        featured_image: featuredImage || undefined,
        status: status as 'draft' | 'published',
        category_ids: selectedCategoryIds
      };
      
      // Only update if editing (we know we have an ID)
      if (isEditing && id) {
        await blogApi.updateBlog(id, blogData);
        toast.success('Changes saved for preview');
        
        // Open preview in new tab - explicitly set noopener and noreferrer
        const previewWindow = window.open(`/admin/blogs/preview/${id}`, '_blank');
        if (previewWindow) {
          previewWindow.opener = null;
        }

        // Return false to prevent any form submission
        return false;
      }
    } catch (error) {
      console.error('Error preparing preview:', error);
      toast.error(`Preview failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
    
    // Return false to ensure no navigation occurs
    return false;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AuthCheck />
      <Card className="mb-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin/blogs')}
              className="mr-2"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <CardTitle>{isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {/* Display the ID button before the Preview button for better visibility */}
            {/* Copy ID button removed 
            {isEditing && id && (
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  // Copy the ID to clipboard
                  navigator.clipboard.writeText(id);
                  toast.success(`ID copied: ${id}`);
                }}
                className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                <span className="mr-1">📋</span> Copy Post ID
              </Button>
            )}
            */}
            
            {/* Standard Preview Button removed
            <Button 
              variant="outline" 
              onClick={async () => {
                console.log("Preview button clicked");
                
                // First ensure we have a valid auth token
                try {
                  console.log("Checking auth session");
                  const { data: sessionData } = await supabase.auth.getSession();
                  console.log("Auth session check result:", sessionData?.session ? "Valid session" : "No session");
                  
                  if (!sessionData || !sessionData.session) {
                    console.error("No valid session found");
                    toast.error("Authentication session expired. Please log in again.");
                    return;
                  }
                  
                  // Optional: refresh the token if needed
                  console.log("Refreshing auth token");
                  const refreshResult = await supabase.auth.refreshSession();
                  console.log("Auth refresh result:", refreshResult.error ? "Error refreshing" : "Success");
                  
                  if (refreshResult.error) {
                    console.error("Failed to refresh auth token:", refreshResult.error);
                    toast.error("Authentication error. Please log in again.");
                    return;
                  }
                  
                  console.log("Auth session refreshed for preview");
                } catch (authError) {
                  console.error("Preview: Auth error:", authError);
                  toast.error("Authentication error. Please log in again.");
                  return;
                }
              
                console.log("Checking if blog is being edited:", isEditing ? "Editing existing" : "Creating new");
                
                // If the blog is not yet saved or has been modified, save it first
                if (!isEditing) {
                  try {
                    console.log("Saving new draft before preview");
                    setSaving(true);
                    toast.info('Saving as draft before preview...');
                    
                    // Prepare blog data with draft status to ensure it's saved
                    const slugToUse = slug || title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\s-]/g, '');
                    console.log("Using slug:", slugToUse);
                    
                    const blogData = {
                      title,
                      slug: slugToUse,
                      content,
                      excerpt: excerpt || undefined,
                      featured_image: featuredImage || undefined,
                      status: 'draft' as 'draft' | 'published',
                      category_ids: selectedCategoryIds
                    };
                    
                    console.log("Blog data prepared:", blogData);
                    
                    if (!user?.id) {
                      console.error("No user ID available");
                      throw new Error('User ID is required to create a blog post');
                    }
                    
                    console.log("Creating blog with user ID:", user.id);
                    
                    // Create the blog post
                    const newBlog = await blogApi.createBlog(blogData, user.id);
                    console.log('Draft created for preview:', newBlog);
                    toast.success('Temporary draft created for preview');
                    
                    // Update local state to reflect we're now editing an existing blog
                    if (newBlog && newBlog.id) {
                      const previewUrl = `/admin/blogs/preview/${newBlog.id}`;
                      console.log("Opening preview URL:", previewUrl);
                      
                      // Use window.open instead of direct navigation
                      window.open(previewUrl, '_blank');
                    } else {
                      console.error("Blog created but no ID returned");
                    }
                  } catch (error) {
                    console.error('Error saving draft for preview:', error);
                    toast.error(`Failed to create preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  } finally {
                    setSaving(false);
                    console.log("Save state reset");
                  }
                } else {
                  // For existing blog posts, ensure any changes are saved first
                  try {
                    console.log("Updating existing blog before preview, ID:", id);
                    setSaving(true);
                    toast.info('Updating draft before preview...');
                    
                    // Prepare update data
                    const blogData = {
                      title,
                      slug,
                      content,
                      excerpt: excerpt || undefined,
                      featured_image: featuredImage || undefined,
                      status: status as 'draft' | 'published',
                      category_ids: selectedCategoryIds
                    };
                    
                    console.log("Update data prepared:", blogData);
                    
                    // Update the blog post
                    const updateResult = await blogApi.updateBlog(id!, blogData);
                    console.log("Update result:", updateResult);
                    toast.success('Changes saved for preview');
                    
                    // Open the preview in a new tab - use direct ID for drafts
                    const previewUrl = `/admin/blogs/preview/${id}`;
                    console.log("Opening preview URL:", previewUrl);
                    
                    // Use window.open instead of direct navigation
                    window.open(previewUrl, '_blank');
                  } catch (error) {
                    console.error('Error updating for preview:', error);
                    toast.error(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  } finally {
                    setSaving(false);
                    console.log("Save state reset");
                  }
                }
              }}
              disabled={!slug || !title || !content || saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </>
              )}
            </Button>
            */}
            
            {/* Direct Preview Button */}
            {isEditing && id && (
              <Button 
                variant="default"
                size="sm"
                onClick={(e) => {
                  // Explicitly prevent default and call handlePreview
                  e.preventDefault();
                  e.stopPropagation();
                  handlePreview(e);
                  // Return false to prevent form submission
                  return false;
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                type="button"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            )}
            
            {/* Save button - stays on the page */}
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e as unknown as React.FormEvent);
                // Return false to prevent form submission
                return false;
              }} 
              disabled={saving || !title || !content}
              type="button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save {status === 'published' ? '& Publish' : 'as Draft'}
            </Button>
            
            {/* Save & Exit button - navigates back to list */}
            <Button 
              variant="outline"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const saveSuccessful = await handleSubmit(e as unknown as React.FormEvent);
                if (saveSuccessful) {
                  // Only navigate if save was successful
                  navigate('/admin/blogs');
                }
                // Return false to prevent form submission
                return false;
              }} 
              disabled={saving || !title || !content}
              type="button"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Save & Exit
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form 
            onSubmit={(e) => {
              // Prevent default form submission
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
              return false;
            }} 
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Title & Slug */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Blog post title"
                  required
                />
              </div>
              <SlugField
                title={title}
                value={slug}
                onChange={setSlug}
                existingId={id}
                required
              />
              
              {/* Status & Categories */}
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={status} 
                  onValueChange={(value) => setStatus(value as 'draft' | 'published')}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categories</Label>
                <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-10">
                  {categories.map((category: BlogCategory) => (
                    <div 
                      key={category.id}
                      className={`
                        px-3 py-1 rounded-full text-sm cursor-pointer transition-colors
                        ${selectedCategoryIds.includes(category.id) 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}
                      `}
                      onClick={() => {
                        setSelectedCategoryIds(prev => 
                          prev.includes(category.id)
                            ? prev.filter(id => id !== category.id)
                            : [...prev, category.id]
                        );
                      }}
                    >
                      {category.name}
                      {selectedCategoryIds.includes(category.id) && (
                        <X className="h-3 w-3 ml-1 inline" />
                      )}
                    </div>
                  ))}
                  
                  {/* New Category Input */}
                  {showNewCategoryInput ? (
                    <form 
                      className="flex gap-2 items-center" 
                      onSubmit={handleCreateCategory}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Input
                        className="h-8 w-40 text-sm"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="New category name"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          } else if (e.key === 'Enter' && newCategoryName.trim()) {
                            e.preventDefault();
                            handleCreateCategory(e);
                          }
                        }}
                      />
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline" 
                        className="h-8"
                        disabled={addingCategory || !newCategoryName.trim()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCreateCategory(e);
                        }}
                      >
                        {addingCategory ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          'Add'
                        )}
                      </Button>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 p-1"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowNewCategoryInput(false);
                          setNewCategoryName('');
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </form>
                  ) : (
                    <div 
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary hover:bg-secondary/80 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowNewCategoryInput(true);
                      }}
                      title="Add new category"
                    >
                      <Plus className="h-4 w-4" />
                    </div>
                  )}
                  
                  {categories.length === 0 && !showNewCategoryInput && (
                    <span className="text-muted-foreground text-sm">No categories available</span>
                  )}
                </div>
              </div>
              
              {/* Excerpt & Featured Image */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="A brief summary of the blog post"
                  rows={3}
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="featuredImage">Featured Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="featuredImage"
                    value={featuredImage}
                    onChange={(e) => setFeaturedImage(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                  <ImageUploader 
                    onImageUploaded={(url) => setFeaturedImage(url)}
                    buttonText="Upload"
                    buttonVariant="outline"
                  />
                </div>
                {featuredImage && (
                  <div className="mt-2 relative w-full h-48 bg-secondary/20 rounded-md overflow-hidden group">
                    <img 
                      src={featuredImage} 
                      alt="Featured" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/600x400?text=Invalid+Image+URL';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setFeaturedImage('')}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="content">Content *</Label>
                <RichTextEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Write your blog content here..."
                />
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default BlogEditor; 